import { useContext, useState, useRef } from "react"

import { UserContext } from "../../../context/userContext"
import HelloSign from "hellosign-embedded"
import { DateTime } from "luxon"

//PrimeReact
import { Dialog } from "primereact/dialog"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { InputText } from "primereact/inputtext"
import { FilterMatchMode } from "primereact/api"
import { Button } from "primereact/button"
import { Card } from "primereact/card"
import { Toast } from "primereact/toast"
import { OverlayPanel } from "primereact/overlaypanel"
import { Panel } from "primereact/panel"

//Docusign components
import { docSigningApiRequestSignaturePost } from "../documentsApi"

//Styles
import docSignStyles from "../../../styles/documentSigning/DocSign.filters.module.css"
import NewRequestSignerRole from "./NewRequestSignerRole"
import { LogError, LogWarning } from "../../../helpers/logger"

const NewRequestForm = ({
  showNewRequest,
  setShowNewRequest,
  templates,
  helloSignConfig,
  participants,
}) => {
  const [globalFilterValue, setGlobalFilterValue] = useState("")
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const [templateInfo, setTemplateInfo] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(undefined)
  const [error, setError] = useState()
  const [submitting, setSubmitting] = useState(false)
  const [collapseTemplatePanel, setCollapseTemplatePanel] = useState(false)
  const userCtx = useContext(UserContext)
  const op = useRef(null)

  const createRequestModel = () => {
    const signers = selectedTemplate?.signerRoles.map((s) => {
      return {
        name: s.participant.name,
        participantId: s.participant.signerId,
        emailAddress: s.participant.email || "test@test.com",
        role: s.name,
        order: 0,
      }
    })

    const customFields = selectedTemplate?.customFields.map((c) => {
      return {
        name: c.name,
        type: c.type,
        value: c.value?.toString(),
        required: c.required,
      }
    })

    return {
      groupId: userCtx.session.user.organization_id,
      signers: signers,
      sender: {
        id: userCtx.session.user.staff_id,
        name:
          userCtx.session.user.staff_name_first +
          " " +
          userCtx.session.user.staff_name_last,
        emailAddress: userCtx.session.user.staff_email_address,
      },
      mergeFields: customFields,
    }
  }

  const checkSelectedParticipants = () => {
    let approved = true
    selectedTemplate?.signerRoles?.forEach((s) => {
      if (!s.participant) {
        approved = false
      }
    })
    return approved
  }

  const checkParticipantSelectionCount = () => {
    let participantCount = 0

    selectedTemplate?.signerRoles?.forEach((s) => {
      if (!!s.participant && !s.participant.signerId.endsWith("-STAFF")) {
        participantCount = participantCount + 1
      }
    })

    return participantCount <= 1
  }

  const validate = () => {
    let err = ""
    if (!selectedTemplate) {
      err = "Must select one template"
    }
    if (!checkSelectedParticipants()) {
      err = "All signer roles must have one participant assigned"
    }
    if (!checkParticipantSelectionCount()) {
      err = "Only one participant is allowed as a signer per signature request"
    }

    setError(err)
    return !err
  }
  const submitNewDocument = async () => {
    if (validate()) {
      setSubmitting(true)
      try {
        const requestModel = createRequestModel()
        const result = await docSigningApiRequestSignaturePost(
          userCtx.tokens.getDocumentSignToken(),
          requestModel,
          selectedTemplate.templateId,
        )
        if (result && result.unclaimedDraft.claimUrl) {
          setShowNewRequest(false)
          const client = new HelloSign({
            clientId: helloSignConfig.clientId,
            skipDomainVerification: helloSignConfig.mode === "Test",
          })

          client.open(result.unclaimedDraft.claimUrl)
          client.on("send", () => {
            userCtx.setNotificationInformation(
              `Your signature-request is being sent in the background. You'll be notified when it's 
              done.`,
              {
                autoClose: 10000,
                pauseOnFocusLoss: false,
              },
            )
          })
          clear()
          setSubmitting(false)
        } else {
          userCtx.setNotificationError(
            "Something went wrong, no claim URL returned",
          )

          setSubmitting(false)
        }
      } catch (error) {
        LogError(
          "An error occurred while trying to request signature(s) for this template",
          error,
        )
        userCtx.setNotificationError(
          "An error occurred while trying to request signature(s) for this template. See console for details.",
        )
        setSubmitting(false)
        return false
      }
    } else {
      LogWarning(
        "There are errors on the form, please correct them and try again.",
      )
    }
  }
  const onGlobalFilterChange = (e) => {
    const value = e.target.value
    let _filters = { ...filters }
    _filters["global"].value = value

    setFilters(_filters)
    setGlobalFilterValue(value)
  }
  const tableHeader = () => {
    return (
      <div>
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search"
          />
        </span>
      </div>
    )
  }
  const checkSelectedTemplate = (templateId) => {
    return selectedTemplate && selectedTemplate.templateId === templateId
  }
  const headerInfo = () => {
    return (
      <div style={{ backgroundColor: "#00b298" }}>
        <p
          style={{
            paddingLeft: "1rem",
            paddingTop: "1rem",
            paddingBottom: "1rem",
            color: "white",
            fontSize: "x-large",
          }}
        >
          Template Details
        </p>
      </div>
    )
  }
  const handleRows = () => {
    if (templates) {
      return templates.items?.map((template) => {
        return {
          template: template.template.title,
          created: DateTime.fromISO(
            template.template.metadata.CreatedAt.split("||")[0],
          ).toLocaleString(),
          actions: (
            <>
              <Button
                icon="pi pi-check-circle"
                onClick={() => {
                  if (!checkSelectedTemplate(template?.template.templateId)) {
                    setSelectedTemplate(template.template)
                  } else {
                    setSelectedTemplate()
                  }
                  setError()
                }}
                style={{
                  backgroundColor:
                    checkSelectedTemplate(template?.templateId) && "red",
                }}
                tooltip="Select"
                tooltipOptions={{
                  position: "bottom",
                  mouseTrack: true,
                  mouseTrackTop: 15,
                }}
                disabled={submitting}
              />
              <>
                <Toast />
                <Button
                  icon="pi pi-info-circle"
                  style={{
                    backgroundColor: "#00b298",
                    marginLeft: "1rem",
                  }}
                  tooltip="Info"
                  tooltipOptions={{
                    position: "bottom",
                    mouseTrack: true,
                    mouseTrackTop: 15,
                  }}
                  onClick={(e) => {
                    op.current.toggle(e)
                    setTemplateInfo(template)
                  }}
                  aria-haspopup
                  aria-controls="overlay_panel"
                  disabled={submitting}
                />
                <OverlayPanel
                  ref={op}
                  showCloseIcon
                  id="overlay_panel"
                  style={{ width: "450px" }}
                  className={docSignStyles.overlaypanel}
                >
                  <Card header={headerInfo()}>
                    <p className={docSignStyles.docTemplateInfo_p}>
                      <b>Title: </b> {templateInfo?.template.title}
                    </p>
                    <p className={docSignStyles.docTemplateInfo_p}>
                      <b>Created: </b>
                      {DateTime.fromISO(
                        templateInfo?.template.metadata.CreatedAt.split(
                          "||",
                        )[0],
                      ).toLocaleString()}
                    </p>
                    <p className={docSignStyles.docTemplateInfo_p}>
                      <b>Documents:</b>
                    </p>
                    <ul className={docSignStyles.documentsListInfo}>
                      {templateInfo?.template.documents?.map((d) => {
                        return <li key={d.name}>{d.name}</li>
                      })}
                    </ul>
                    <p className={docSignStyles.docTemplateInfo_p}>
                      <b>Signer Roles:</b>
                    </p>
                    <ul className={docSignStyles.documentsListInfo}>
                      {templateInfo?.template.signerRoles?.map((d) => {
                        return <li key={d.name}>{d.name}</li>
                      })}
                    </ul>
                    <p className={docSignStyles.docTemplateInfo_p}>
                      <b>Custom Fields:</b>
                    </p>
                    <ul className={docSignStyles.documentsListInfo}>
                      {templateInfo?.template.customFields?.map((d) => {
                        return <li key={d.name}>{d.name}</li>
                      })}
                    </ul>
                  </Card>
                </OverlayPanel>
              </>
            </>
          ),
        }
      })
    }
  }
  const modalFooter = () => {
    return (
      <div>
        <Button
          label="Next"
          onClick={() => {
            submitNewDocument()
          }}
          disabled={submitting}
        />
        <Button
          label="Cancel"
          style={{ backgroundColor: "red" }}
          onClick={() => {
            clear()
            setShowNewRequest(false)
          }}
          disabled={submitting}
        />
      </div>
    )
  }
  const clear = () => {
    //Clear signerRoles for the selected template
    if (!!selectedTemplate) {
      selectedTemplate.signerRoles?.forEach((role) => (role.participant = null))
    }
    setSelectedTemplate(undefined)
    setError()
  }

  const updateSelectedTemplate = (templateProfile) => {
    setSelectedTemplate(templateProfile)
    setError()
  }

  return (
    <Dialog
      closable={!submitting}
      header={"Request Signatures"}
      visible={showNewRequest}
      onHide={() => {
        setShowNewRequest(false)
      }}
      style={{ width: "600px", height: "900px" }}
      footer={modalFooter()}
    >
      <>
        <Panel
          header={"Select Template"}
          toggleable
          collapsed={collapseTemplatePanel}
          onToggle={(e) => setCollapseTemplatePanel(e.value)}
        >
          <DataTable
            value={handleRows()}
            paginator
            header={tableHeader}
            rows={10}
            responsiveLayout="scroll"
            sortField="template"
            sortOrder={1}
          >
            <Column field="template" header="Template" align="center"></Column>
            <Column field="created" header="Created" align="center"></Column>
            <Column field="actions" header="Actions" align="center"></Column>
          </DataTable>
        </Panel>
        <Panel
          header={
            !!selectedTemplate
              ? `Signer Roles: ${selectedTemplate?.title}`
              : "Signer Roles"
          }
        >
          {!!selectedTemplate ? (
            <>
              <span
                style={{
                  width: "20rem",
                  fontSize: "smaller",
                  color: "grey",
                }}
              >
                Select a participant for each signer-role in this template.
              </span>
              {selectedTemplate?.signerRoles?.map((d, index) => {
                return (
                  <span key={index}>
                    <NewRequestSignerRole
                      documentTemplate={selectedTemplate}
                      users={participants}
                      signerRoleProfile={d}
                      updateDocumentTemplateCallBack={updateSelectedTemplate}
                      disabled={submitting}
                    />
                  </span>
                )
              })}
            </>
          ) : (
            <>Please select a template to continue</>
          )}
        </Panel>
        {error && (
          <span style={{ color: "red", marginLeft: "1rem" }}>{error}</span>
        )}
      </>
    </Dialog>
  )
}
export default NewRequestForm
